import axios, { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios'
import * as t from 'io-ts'
import { taskEither, either, task, io, option, array } from 'fp-ts'
import { flow, identity, pipe } from 'fp-ts/lib/function'
import { failure } from 'io-ts/lib/PathReporter'
export { CancelToken } from 'axios'
import { NumberFromString } from 'io-ts-types/lib/NumberFromString'

type RequestConfig<A, B = unknown> = AxiosRequestConfig & {
  encoder?: t.Encoder<B, any>
  decoder: t.Decoder<unknown, A>
}

export const getCookie = (cname: string) => {
  const name = `${cname}=`
  const decodedCookie = decodeURIComponent(document.cookie)
  const ca = decodedCookie.split(';')
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i]
    while (c.charAt(0) === ' ') {
      c = c.substring(1)
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length)
    }
  }
  return ''
}

const setCookie =
  (cookie: string): io.IO<void> =>
  () => {
    document.cookie = cookie
  }

const clearCookie = (cname: string): io.IO<void> =>
  setCookie(`${cname}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`)

export const clearCookies = (): io.IO<void> =>
  pipe(
    ['proof_of_ownership', 'access_token', 'session_token'],
    array.map(clearCookie),
    array.Traversable.sequence(io.Monad)
  )

const interceptAndGetAuthorization = (res: AxiosResponse) =>
  pipe(
    option.fromNullable(res.headers),
    option.map(t.type({ authorization: t.string }).decode),
    option.chain(option.fromEither),
    option.map(hs => () => {
      document.cookie = `authorizationBearer=${hs.authorization}`
    }),
    option.Traversable.sequence(io.Monad),
    io.map(() => res)
  )

const interceptAndGetTotalCount = (res: AxiosResponse) =>
  pipe(
    option.fromNullable(res.headers),
    option.map(t.type({ 'x-total-count': NumberFromString }).decode),
    option.chain(option.fromEither),
    option.map(hs => ({
      ...res,
      data: { data: res.data, count: hs['x-total-count'] },
    })),
    option.getOrElse(() => res)
  )

axios.defaults.withCredentials = true

axios.interceptors.response.use(r =>
  pipe(r, interceptAndGetTotalCount, interceptAndGetAuthorization)()
)

export const isAxiosError = (u: unknown): u is AxiosError =>
  u instanceof Object && u instanceof Error && 'isAxiosError' in u

const AxiosErrorC = new t.Type<AxiosError, AxiosError, unknown>(
  'AxiosError',
  isAxiosError,
  (u, c) => (isAxiosError(u) ? t.success(u) : t.failure(u, c)),
  t.identity
)

export const validationErrToErr = (es: t.ValidationError[]) =>
  new TypeError(failure(es).join('\n'))

const axiosErr = flow(
  AxiosErrorC.decode,
  either.fold(validationErrToErr, identity)
)

const decodeResponse = <A>(decoder: t.Decoder<unknown, A>) =>
  taskEither.chain((x: AxiosResponse<any>) =>
    pipe(
      task.of(decoder.decode(x.data)),
      taskEither.mapLeft(validationErrToErr)
    )
  )

export const get = <A>(
  url: string,
  { decoder, ...config }: RequestConfig<A>
): taskEither.TaskEither<Error, A> =>
  pipe(
    taskEither.tryCatch(() => axios.get(url, config), axiosErr),
    decodeResponse(decoder)
  )

export const delete_ = <A>(
  url: string,
  { decoder, ...config }: RequestConfig<A>
): taskEither.TaskEither<Error, A> =>
  pipe(
    taskEither.tryCatch(() => axios.delete(url, config), axiosErr),
    decodeResponse(decoder)
  )

export const post = <A, B>(
  url: string,
  body: B,
  { decoder, encoder, ...config }: RequestConfig<A, B>
): taskEither.TaskEither<Error, A> =>
  pipe(
    taskEither.tryCatch(
      () => axios.post(url, !!encoder ? encoder.encode(body) : body, config),
      axiosErr
    ),
    decodeResponse(decoder)
  )

export const put = <A, B>(
  url: string,
  body: B,
  { decoder, encoder, ...config }: RequestConfig<A, B>
): taskEither.TaskEither<Error, A> =>
  pipe(
    taskEither.tryCatch(
      () => axios.put(url, !!encoder ? encoder.encode(body) : body, config),
      axiosErr
    ),
    decodeResponse(decoder)
  )

export const mockCall = <A>(
  res: A,
  delay = 2000
): taskEither.TaskEither<Error, A> =>
  pipe(taskEither.right(res), task.delay(Math.random() * delay))

export const throwLeft: <L, R>(
  fa: taskEither.TaskEither<L, R>
) => task.Task<R> = task.map(
  either.fold(
    e => {
      throw e
    },
    x => x
  )
)

export const catchNotFound = <E, T>(
  te: taskEither.TaskEither<E, T>
): taskEither.TaskEither<E, T | 'Not Found'> =>
  pipe(
    te,
    task.map(
      either.fold(
        e => {
          if (isAxiosError(e) && e.response?.status === 404) {
            return either.right('Not Found')
          } else {
            return either.left(e)
          }
        },
        r => either.right(r)
      )
    )
  )
